import { useState } from "react";
import { Bubble, Sender } from "@ant-design/x";
import { Card } from "@rumbledirect/ui";
import { Flex, Spin, theme, Typography } from "antd";
import { sendMessage } from "../../../lib/api/resources/agent";
import { parseReply } from "../agents/lib/parseReply";
import markdownit from "markdown-it";
import { useMutation, useQuery } from "@tanstack/react-query";

interface Message {
  role: "user" | "assistant";
  content: string;
  loading?: boolean;
  typing?: boolean;
}

const AGENT_ID = "00000000-0000-7000-8000-001000000005";

const md = markdownit({ html: true, breaks: true });

const renderMarkdown = (content: string, role: "user" | "assistant") => {
  return (
    <Typography>
      <div
        style={{ color: role === "user" ? "rgb(255, 79, 20)" : "#000000" }}
        dangerouslySetInnerHTML={{ __html: md.render(content) }}
      />
    </Typography>
  );
};

export const KnowledgeBase = () => {
  const { token } = theme.useToken();
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [senderValue, setSenderValue] = useState<string>("");

  const { isLoading: isInitializing } = useQuery({
    queryKey: ["knowledgeBaseSession", "initial"],
    queryFn: async () => {
      const response = await sendMessage("", AGENT_ID, null);
      const newSessionId = response.headers["x-session-id"];
      const message = parseReply(response);

      setSessionId(newSessionId);
      setMessages([{ role: "assistant", content: message }]);

      return { sessionId: newSessionId, message };
    },
    enabled: !sessionId,
    staleTime: Infinity,
  });

  const sendMessageMutation = useMutation({
    mutationFn: async (content: string) => {
      const response = await sendMessage(content, AGENT_ID, sessionId);
      return parseReply(response);
    },
    onMutate: async (content: string) => {
      setSenderValue("");
      setMessages((prev) => [...prev, { role: "user", content }]);
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: "", loading: true },
      ]);
    },
    onSuccess: (reply) => {
      setMessages((prev) => {
        const newMessages = [...prev];
        newMessages[newMessages.length - 1] = {
          role: "assistant",
          content: reply,
          loading: false,
          typing: true,
        };
        return newMessages;
      });
    },
    onError: () => {
      setMessages((prev) => prev.slice(0, -2));
      setSenderValue(senderValue);
    },
  });

  const handleSendMessage = (content: string) => {
    if (!content.trim() || !sessionId) return;
    sendMessageMutation.mutate(content);
  };

  return (
    <Card title="Kennisbank">
      <Spin spinning={isInitializing}>
        <div style={{ height: "calc(100vh - 250px)" }}>
          <Flex vertical justify="space-between" style={{ height: "100%" }}>
            <Bubble.List
              autoScroll
              items={messages.map((message, index) => ({
                key: index,
                messageRender: (content) =>
                  renderMarkdown(content, message.role),
                content: message.content,
                placement: message.role === "user" ? "end" : "start",
                loading: message.loading,
                typing: message.typing ? { step: 5, interval: 20 } : undefined,
                styles:
                  message.role === "user"
                    ? {
                        content: {
                          backgroundColor: "#fff0e6",
                          paddingBottom: 8,
                        },
                      }
                    : {
                        content: {
                          paddingBottom: 8,
                        },
                      },
              }))}
            />
            <Sender
              style={{ marginTop: token.marginMD }}
              value={senderValue}
              onChange={setSenderValue}
              onSubmit={handleSendMessage}
              loading={sendMessageMutation.isPending}
              disabled={!sessionId || isInitializing}
              placeholder="Stel een vraag..."
            />
          </Flex>
        </div>
      </Spin>
    </Card>
  );
};
